import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ConsentBanner } from "@/components/ConsentBanner";
import MaintenancePage from "@/pages/MaintenancePage";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import EmailVerify from "@/pages/EmailVerify";
import DiagnosticStart from "@/pages/DiagnosticStart";
import DiagnosticSession from "@/pages/DiagnosticSession";
import DiagnosticResults from "@/pages/DiagnosticResults";
import { Privacy, Terms, KVKK } from "@/pages/legal/Legal";

import Panel from "@/pages/dashboard/Panel";
import Chat from "@/pages/dashboard/Chat";
import History from "@/pages/dashboard/History";
import Profile from "@/pages/dashboard/Profile";
import Subscription from "@/pages/dashboard/Subscription";
import SubscriptionCallback from "@/pages/dashboard/SubscriptionCallback";

import AdminHome from "@/pages/admin/AdminHome";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminSayisalPackages from "@/pages/admin/AdminSayisalPackages";
import AdminAiTelemetry from "@/pages/admin/AdminAiTelemetry";
import AdminWaitlist from "@/pages/admin/AdminWaitlist";
import AdminSayisalFunnel from "@/pages/admin/AdminSayisalFunnel";

import Demo from "@/pages/Demo";

import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// Build-time flag — flip via frontend/.env REACT_APP_MAINTENANCE_MODE (requires rebuild)
const BUILD_MAINTENANCE = process.env.REACT_APP_MAINTENANCE_MODE === "true";

function useMaintenanceMode() {
  // If build-time flag is on, skip network — show maintenance immediately (no flash).
  const [mode, setMode] = useState(BUILD_MAINTENANCE ? "on" : "checking");

  useEffect(() => {
    if (BUILD_MAINTENANCE) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/config`, { credentials: "omit" });
        if (!res.ok) throw new Error("config unreachable");
        const cfg = await res.json();
        if (cancelled) return;
        setMode(cfg?.maintenance_mode === true ? "on" : "off");
      } catch {
        // If backend is unreachable, default to normal app rather than blocking users
        if (!cancelled) setMode("off");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return mode;
}

function App() {
  const mode = useMaintenanceMode();

  // Brief loading shim (only when runtime-checking). Preserves layout so analytics still fire.
  if (mode === "checking") {
    return <div className="min-h-screen bg-white" data-testid="config-loading" />;
  }

  if (mode === "on") {
    return (
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <Toaster position="top-center" richColors closeButton />
            <ConsentBanner />
            <Routes>
              {/* Legal pages remain reachable so verification tags stay valid and users can read privacy */}
              <Route path="/gizlilik" element={<Privacy />} />
              <Route path="/kullanim-sartlari" element={<Terms />} />
              <Route path="/kvkk" element={<KVKK />} />

              {/* Auth remains reachable so admins (and password-reset flows) can still sign in */}
              <Route path="/giris" element={<Login />} />
              <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
              <Route path="/sifre-sifirla" element={<ResetPassword />} />
              <Route path="/email-dogrula" element={<EmailVerify />} />

              {/* Admin panel stays fully accessible — regular students who log in still hit the
                  maintenance wildcard below because /panel/* isn't listed here. */}
              <Route path="/admin" element={<ProtectedRoute adminOnly><AdminHome /></ProtectedRoute>} />
              <Route path="/admin/kullanicilar" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
              <Route path="/admin/abonelikler" element={<ProtectedRoute adminOnly><AdminSubscriptions /></ProtectedRoute>} />
              <Route path="/admin/sayisal-paketler" element={<ProtectedRoute adminOnly><AdminSayisalPackages /></ProtectedRoute>} />
              <Route path="/admin/ai-telemetri" element={<ProtectedRoute adminOnly><AdminAiTelemetry /></ProtectedRoute>} />
              <Route path="/admin/bekleme-listesi" element={<ProtectedRoute adminOnly><AdminWaitlist /></ProtectedRoute>} />
              <Route path="/admin/sayisal-huni" element={<ProtectedRoute adminOnly><AdminSayisalFunnel /></ProtectedRoute>} />

              {/* Everything else → maintenance page (HTTP 200) */}
              <Route path="*" element={<MaintenancePage />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" richColors closeButton />
          <ConsentBanner />
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/giris" element={<Login />} />
            <Route path="/kayit" element={<Register />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
            <Route path="/sifre-sifirla" element={<ResetPassword />} />
            <Route path="/email-dogrula" element={<EmailVerify />} />
            <Route path="/gizlilik" element={<Privacy />} />
            <Route path="/kullanim-sartlari" element={<Terms />} />
            <Route path="/kvkk" element={<KVKK />} />

            {/* Public Sayısal V1 free diagnostic funnel (Track A: state only) */}
            <Route path="/tanilama" element={<DiagnosticStart />} />
            <Route path="/tanilama/sonuc" element={<DiagnosticResults />} />
            <Route path="/tanilama/:sessionId" element={<DiagnosticSession />} />

            {/* Student protected */}
            <Route path="/panel" element={<ProtectedRoute><Panel /></ProtectedRoute>} />
            <Route path="/panel/sohbet" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
            <Route path="/panel/gecmis" element={<ProtectedRoute><History /></ProtectedRoute>} />
            <Route path="/panel/profil" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/panel/abonelik" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
            <Route path="/abonelik/callback" element={<ProtectedRoute><SubscriptionCallback /></ProtectedRoute>} />

            {/* Admin protected */}
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminHome /></ProtectedRoute>} />
            <Route path="/admin/kullanicilar" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/abonelikler" element={<ProtectedRoute adminOnly><AdminSubscriptions /></ProtectedRoute>} />
            <Route path="/admin/sayisal-paketler" element={<ProtectedRoute adminOnly><AdminSayisalPackages /></ProtectedRoute>} />
            <Route path="/admin/ai-telemetri" element={<ProtectedRoute adminOnly><AdminAiTelemetry /></ProtectedRoute>} />
            <Route path="/admin/bekleme-listesi" element={<ProtectedRoute adminOnly><AdminWaitlist /></ProtectedRoute>} />
            <Route path="/admin/sayisal-huni" element={<ProtectedRoute adminOnly><AdminSayisalFunnel /></ProtectedRoute>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
